import { useCallback, useEffect, useRef, useState } from "react";

/** A still pointer gives up the aim, as Radix and Base UI submenus do. */
const REST_MS = 40;

const HOVER_EVENTS = [
	"pointerover",
	"pointerenter",
	"pointermove",
	"mouseover",
	"mouseenter",
	"mousemove",
] as const;

interface Point {
	readonly x: number;
	readonly y: number;
}

type Box = Pick<DOMRect, "left" | "right" | "top" | "bottom">;

const side = (p: Point, a: Point, b: Point) =>
	(p.x - b.x) * (a.y - b.y) - (a.x - b.x) * (p.y - b.y);

const inBox = (p: Point, box: Box) =>
	p.x >= box.left && p.x <= box.right && p.y >= box.top && p.y <= box.bottom;

/**
 * Whether a pointer at `point`, having left its trigger at `tip`, is heading
 * into a popup beside that trigger: inside the triangle from the tip to the
 * popup's near edge, or in the gap between trigger and popup.
 */
export function headingIntoPopup(
	point: Point,
	tip: Point,
	trigger: Box,
	popup: Box,
): boolean {
	const toRight = popup.left >= trigger.right - 1;
	if (!toRight && popup.right > trigger.left + 1) return false;
	const edge = toRight ? popup.left : popup.right;
	const near = toRight ? trigger.right : trigger.left;
	const gap = {
		left: Math.min(edge, near),
		right: Math.max(edge, near),
		top: popup.top,
		bottom: popup.bottom,
	};
	if (inBox(point, gap)) return true;
	const top = { x: edge, y: popup.top };
	const bottom = { x: edge, y: popup.bottom };
	const d1 = side(point, tip, top);
	const d2 = side(point, top, bottom);
	const d3 = side(point, bottom, tip);
	const negative = d1 < 0 || d2 < 0 || d3 < 0;
	const positive = d1 > 0 || d2 > 0 || d3 > 0;
	return !(negative && positive);
}

const pointOf = (event: MouseEvent): Point => ({
	x: event.clientX,
	y: event.clientY,
});

const isMouse = (event: Event) =>
	"pointerType" in event && event.pointerType === "mouse";

/**
 * The safe triangle for hover flyouts built outside Radix menus: while the
 * mouse travels from an open trigger into its popup, the other triggers in
 * the container ignore it, so the rows it crosses neither highlight nor take
 * the popup. Leaving the triangle, landing in the popup, or resting for 40ms
 * lets go. The open trigger marks itself with `data-popup-open`.
 *
 * Put `containerRef` on the element holding the triggers and `popupRef` on
 * the popup they open.
 */
export function useSubmenuAim() {
	const popup = useRef<HTMLElement | null>(null);

	const popupRef = useCallback((element: HTMLElement | null) => {
		if (element !== null) popup.current = element;
		else if (popup.current?.isConnected === false) popup.current = null;
	}, []);

	const [container, containerRef] = useState<HTMLElement | null>(null);

	useEffect(() => {
		if (container === null) return;
		const doc = container.ownerDocument;
		let tip: Point | null = null;
		let trigger: HTMLElement | null = null;
		let aiming = false;
		let rest: ReturnType<typeof setTimeout> | undefined;

		const heading = (point: Point) => {
			const target = popup.current;
			return (
				tip !== null &&
				trigger?.isConnected === true &&
				target?.isConnected === true &&
				headingIntoPopup(
					point,
					tip,
					trigger.getBoundingClientRect(),
					target.getBoundingClientRect(),
				)
			);
		};
		const release = () => {
			aiming = false;
			clearTimeout(rest);
			container.style.pointerEvents = "";
			if (trigger !== null) trigger.style.pointerEvents = "";
			doc.removeEventListener("pointermove", follow);
			// Only a fresh pass over the open trigger aims again.
			tip = null;
			trigger = null;
		};
		const follow = (event: PointerEvent) => {
			const landed =
				event.target instanceof Node &&
				popup.current?.contains(event.target) === true;
			if (landed || !heading(pointOf(event))) {
				release();
				return;
			}
			clearTimeout(rest);
			rest = setTimeout(release, REST_MS);
		};
		const guard = (event: MouseEvent) => {
			const open =
				event.target instanceof Element
					? event.target.closest<HTMLElement>("[data-popup-open]")
					: null;
			if (open !== null && container.contains(open)) {
				if (aiming) release();
				if (isMouse(event)) {
					tip = pointOf(event);
					trigger = open;
				}
				return;
			}
			if (!aiming) {
				if (!isMouse(event) || !heading(pointOf(event))) return;
				aiming = true;
				// Only the open trigger stays hoverable, so letting go
				// re-enters whatever row is underneath.
				container.style.pointerEvents = "none";
				if (trigger !== null) trigger.style.pointerEvents = "auto";
				doc.addEventListener("pointermove", follow);
				rest = setTimeout(release, REST_MS);
			}
			event.stopPropagation();
		};

		const listening = new AbortController();
		for (const type of HOVER_EVENTS)
			container.addEventListener(type, guard, {
				capture: true,
				signal: listening.signal,
			});
		return () => {
			release();
			listening.abort();
		};
	}, [container]);

	return { containerRef, popupRef };
}
